// Lightweight Google Maps JS loader with graceful fallback.
// Reads key from /api/config so it can be swapped at any time via backend/.env.
// If the key is missing, we simply do not load the SDK — callers must gate on isReady.
import { useEffect, useState } from "react";

let _loaderPromise = null;
let _cachedConfig = null;

async function fetchConfig() {
  if (_cachedConfig) return _cachedConfig;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/config`;
  const res = await fetch(url);
  _cachedConfig = await res.json();
  return _cachedConfig;
}

function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,marker&v=weekly&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return _loaderPromise;
}

export function useGoogleMaps() {
  const [state, setState] = useState({
    isReady: false, isLoading: true, error: null, hasKey: false, config: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchConfig();
        const key = cfg?.google_maps_api_key || "";
        if (!key) {
          if (!cancelled) setState({ isReady: false, isLoading: false, error: null, hasKey: false, config: cfg });
          return;
        }
        await loadGoogleMaps(key);
        if (!cancelled) setState({ isReady: true, isLoading: false, error: null, hasKey: true, config: cfg });
      } catch (e) {
        if (!cancelled) setState({ isReady: false, isLoading: false, error: e.message || "Google Maps failed", hasKey: true, config: _cachedConfig });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

// Haversine fallback (used when Directions/Routes API not available)
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2 +
             Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s1), Math.sqrt(1 - s1));
}
